import React from "react";
import { Card, Row, Col, Typography, Statistic } from "antd";
import {
  ApiOutlined,
  EditOutlined,
  SettingOutlined,
  NotificationOutlined,
  TeamOutlined,
  BarChartOutlined,
  GiftOutlined,
  FileTextOutlined,
  QrcodeOutlined,
  CarOutlined,
  FlagOutlined,
  GlobalOutlined,
  DesktopOutlined,
  AppstoreOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import theme from "../config/theme";

const { Title, Text } = Typography;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const quickActions = [
    {
      title: t("edit_menu"),
      description: t("edit_menu_description"),
      icon: (
        <EditOutlined style={{ fontSize: theme.iconSizes.xl, color: theme.colors.primary.main }} />
      ),
      path: "/admin/editor",
      color: theme.colors.primary.main,
    },
    {
      title: t("manage_members"),
      description: t("manage_members_description"),
      icon: <TeamOutlined style={{ fontSize: theme.iconSizes.xl, color: theme.colors.success }} />,
      path: "/admin/members",
      color: theme.colors.success,
    },
    {
      title: t("statistics"),
      description: t("statistics_description"),
      icon: <BarChartOutlined style={{ fontSize: theme.iconSizes.xl, color: theme.colors.info }} />,
      path: "/admin/stats",
      color: theme.colors.info,
    },
  ];

  const allFunctions = [
    {
      title: t("user_management"),
      icon: <UserOutlined />,
      path: "/admin/users",
    },
    {
      title: t("edit_customization_options"),
      icon: <SettingOutlined />,
      path: "/admin/item-modification-schema",
    },
    {
      title: t("combo_addons"),
      icon: <ShoppingOutlined />,
      path: "/admin/combo-addons",
    },
    {
      title: t("announcement_settings"),
      icon: <NotificationOutlined />,
      path: "/admin/announcements",
    },
    {
      title: t("reports"),
      icon: <FileTextOutlined />,
      path: "/admin/reports",
    },
    {
      title: t("point_gift_cards"),
      icon: <GiftOutlined />,
      path: "/admin/point-gift-cards",
    },
    {
      title: t("in_store_qr_code"),
      icon: <QrcodeOutlined />,
      path: "/admin/qr-code",
    },
    {
      title: t("square_integrations"),
      icon: <ApiOutlined />,
      path: "/admin/square-integrations",
    },
    {
      title: t("workstation_schemas"),
      icon: <DesktopOutlined />,
      path: "/admin/workstation-schemas",
    },
    {
      title: t("item_schema_groups"),
      icon: <AppstoreOutlined />,
      path: "/admin/item-schema-groups",
    },
    {
      title: t("restaurant_public_info"),
      icon: <ShopOutlined />,
      path: "/admin/restaurant-public-info",
    },
    {
      title: t("points_config"),
      icon: <GiftOutlined />,
      path: "/admin/points-config",
    },
    {
      title: t("wait_config"),
      icon: <FieldTimeOutlined />,
      path: "/admin/wait-config",
    },
    {
      title: t("large_volume_delivery"),
      icon: <CarOutlined />,
      path: "/admin/large-volume-delivery",
    },
    {
      title: t("large_volume_pickup_config"),
      icon: <ShoppingCartOutlined />,
      path: "/admin/large-volume-pickup-config",
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: theme.spacing.xxl }}>
        <Title level={2} style={{ margin: 0, color: theme.colors.text.primary }}>
          {t("welcome_admin_dashboard")}
        </Title>
        <Text
          style={{ fontSize: theme.typography.fontSize.lg, color: theme.colors.text.secondary }}
        >
          {t("admin_dashboard_welcome_description")}
        </Text>
      </div>

      {/* Quick Actions */}
      <div style={{ marginBottom: theme.spacing.xxl }}>
        <Title level={3} style={{ marginBottom: theme.spacing.lg }}>
          {t("quick_actions")}
        </Title>
        <Row gutter={[24, 24]}>
          {quickActions.map((action) => (
            <Col xs={24} sm={12} lg={6} key={action.path}>
              <Card
                hoverable
                onClick={() => navigate(action.path)}
                style={{
                  borderRadius: theme.borderRadius.xl,
                  boxShadow: theme.shadows.base,
                  border: `2px solid ${theme.colors.neutral.gray200}`,
                  cursor: "pointer",
                  height: "100%",
                }}
                styles={{
                  body: {
                    padding: theme.spacing.xl,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: theme.layout.card.minHeight,
                  },
                }}
              >
                <div style={{ marginBottom: theme.spacing.md }}>{action.icon}</div>
                <Title level={5} style={{ margin: 0, marginBottom: theme.spacing.xs }}>
                  {action.title}
                </Title>
                <Text type="secondary" style={{ fontSize: theme.typography.fontSize.sm }}>
                  {action.description}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* All Functions */}
      <div>
        <Title level={3} style={{ marginBottom: theme.spacing.lg }}>
          {t("all_functions")}
        </Title>
        <Row gutter={[16, 16]}>
          {allFunctions.map((func) => (
            <Col xs={24} sm={12} md={8} lg={6} key={func.path}>
              <Card
                hoverable
                onClick={() => navigate(func.path)}
                style={{
                  borderRadius: theme.borderRadius.lg,
                  boxShadow: theme.shadows.base,
                  cursor: "pointer",
                  border: `1px solid ${theme.colors.neutral.gray200}`,
                }}
                styles={{
                  body: {
                    padding: theme.spacing.lg,
                    display: "flex",
                    alignItems: "center",
                    gap: theme.spacing.md,
                  },
                }}
              >
                <div style={{ fontSize: theme.iconSizes.lg, color: theme.colors.primary.main }}>
                  {func.icon}
                </div>
                <Text strong style={{ fontSize: theme.typography.fontSize.base }}>
                  {func.title}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

export default AdminDashboard;
